/**
 * UI-6b Task 9 — ConversationVolume widget renderer.
 *
 * Line chart of call counts over time, sourced from
 * /reports/call-volume?from=<N-days-ago>&to=<now>&groupBy=day (or hour for
 * 1-day windows). The backend returns one row per period bucket; we plot
 * `count`.
 *
 * Config: { days: number } — defaults to 7. Picks `groupBy=hour` when
 * days==1 so a 24h view actually shows hourly buckets; otherwise `day`.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import { api } from '../../../../api/client.js'
import { formatInTimezone, timezoneDayKey } from '../../../../utils/timezone.js'

export interface ConversationVolumeConfig {
  days: number
}

interface CallVolumeRow {
  period: string
  count: number | string
  answered?: number | string
  missed?: number | string
}

interface CallVolumeResponse {
  rows: CallVolumeRow[]
}

function buildRange(days: number): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000)
  return { from: from.toISOString(), to: to.toISOString() }
}

function toNumber(v: number | string | null | undefined): number {
  if (v == null) return 0
  return typeof v === 'number' ? v : Number.parseInt(v, 10) || 0
}

function formatPeriod(p: string, groupBy: string): string {
  // Axis labels follow the user's selected timezone; `toISOString()` pinned
  // every chart to UTC no matter who was reading it.
  if (groupBy === 'hour') {
    return formatInTimezone(
      p, { hour: '2-digit', minute: '2-digit', hour12: false }, 'en-GB',
    ) || p
  }
  return timezoneDayKey(p) || p
}

export function ConversationVolumeRenderer({
  config,
}: {
  config: ConversationVolumeConfig
}): JSX.Element {
  const [rows, setRows] = useState<CallVolumeRow[] | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const days = config.days > 0 ? config.days : 7
  const groupBy = days <= 1 ? 'hour' : 'day'

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    const { from, to } = buildRange(days)
    const query = new URLSearchParams({ from, to, groupBy })

    api
      .get<CallVolumeResponse>(`/reports/call-volume?${query.toString()}`)
      .then((resp) => {
        if (!cancelled) setRows(resp.rows)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load call volume')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return (): void => {
      cancelled = true
    }
  }, [days, groupBy])

  const chartData = useMemo(() => {
    if (!rows) return null
    return {
      labels: rows.map((r) => formatPeriod(r.period, groupBy)),
      datasets: [
        {
          label: 'Conversations',
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99,102,241,0.2)',
          tension: 0.3,
          fill: true,
          data: rows.map((r) => toNumber(r.count)),
        },
      ],
    }
  }, [rows, groupBy])

  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    }),
    [],
  )

  if (loading) {
    return (
      <div data-testid="conversation-volume-loading" className="text-sm text-gray-500">
        Loading conversation volume…
      </div>
    )
  }
  if (error) {
    return (
      <div data-testid="conversation-volume-error" className="text-sm text-red-600">
        {error}
      </div>
    )
  }
  if (!rows || rows.length === 0) {
    return (
      <div data-testid="conversation-volume-empty" className="text-sm text-gray-500">
        No conversation activity in the selected window.
      </div>
    )
  }

  if (!chartData) return <></>

  return (
    <div data-testid="conversation-volume-widget" className="h-full w-full">
      <Chart type="line" data={chartData} options={chartOptions} />
    </div>
  )
}
